import type { TrainingAiProfileRecord, TrainingGameRecord, TrainingMoveRecord } from './trainingTypes';

const DB_NAME = 'omulongo_training';
const DB_VERSION = 2;
const STORE_GAMES = 'games';
const STORE_PROFILES = 'profiles';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_GAMES)) {
        const store = db.createObjectStore(STORE_GAMES, { keyPath: 'id' });
        store.createIndex('syncedAt', 'syncedAt');
        store.createIndex('status', 'status');
      }
      if (!db.objectStoreNames.contains(STORE_PROFILES)) {
        const store = db.createObjectStore(STORE_PROFILES, { keyPath: 'id' });
        store.createIndex('syncedAt', 'syncedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
  });
}

async function withStore<T>(storeName: string, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      reject(tx.error || new Error('IndexedDB transaction failed'));
      db.close();
    };
  });
}

export async function upsertTrainingGame(game: TrainingGameRecord): Promise<void> {
  await withStore(STORE_GAMES, 'readwrite', (store) => store.put(game));
}

export async function getTrainingGame(id: string): Promise<TrainingGameRecord | null> {
  const game = await withStore<TrainingGameRecord | undefined>(STORE_GAMES, 'readonly', (store) => store.get(id));
  return game || null;
}

export async function appendTrainingMove(gameId: string, move: TrainingMoveRecord): Promise<void> {
  const current = await getTrainingGame(gameId);
  if (!current) return;
  const next: TrainingGameRecord = {
    ...current,
    moves: [...current.moves, move],
    updatedAt: new Date().toISOString(),
  };
  await upsertTrainingGame(next);
}

export async function listUnsyncedCompletedGames(limit = 10): Promise<TrainingGameRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_GAMES, 'readonly');
    const store = tx.objectStore(STORE_GAMES);
    const req = store.openCursor();
    const out: TrainingGameRecord[] = [];
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(out);
        return;
      }
      const value = cursor.value as TrainingGameRecord;
      if (!value.syncedAt && value.status === 'COMPLETED') {
        out.push(value);
      }
      if (out.length >= limit) {
        resolve(out);
        return;
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error || new Error('Failed to read IndexedDB cursor'));
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      reject(tx.error || new Error('IndexedDB transaction failed'));
      db.close();
    };
  });
}

export async function markTrainingGameSynced(id: string): Promise<void> {
  const current = await getTrainingGame(id);
  if (!current) return;
  await upsertTrainingGame({ ...current, syncedAt: new Date().toISOString() });
}

export async function upsertAiProfile(profile: TrainingAiProfileRecord): Promise<void> {
  await withStore(STORE_PROFILES, 'readwrite', (store) => store.put(profile));
}

export async function listUnsyncedProfiles(limit = 10): Promise<TrainingAiProfileRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROFILES, 'readonly');
    const store = tx.objectStore(STORE_PROFILES);
    const req = store.openCursor();
    const out: TrainingAiProfileRecord[] = [];
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(out);
        return;
      }
      const value = cursor.value as TrainingAiProfileRecord;
      if (!value.syncedAt) {
        out.push(value);
      }
      if (out.length >= limit) {
        resolve(out);
        return;
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error || new Error('Failed to read IndexedDB cursor'));
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      reject(tx.error || new Error('IndexedDB transaction failed'));
      db.close();
    };
  });
}

export async function markProfileSynced(id: string): Promise<void> {
  const current = await withStore<TrainingAiProfileRecord | undefined>(STORE_PROFILES, 'readonly', (store) => store.get(id));
  if (!current) return;
  await upsertAiProfile({ ...current, syncedAt: new Date().toISOString() });
}
